import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

// Nano Quest admin, for the Communications team. The dashboard server proxies
// /api/quest-admin to the tech server's /api/quest/admin, which gates every
// call on NEO2-Communications-Access (full admins pass).

export function useQuestAdminAccess() {
  return useQuery({
    queryKey: ['quest', 'admin', 'access'],
    queryFn: async () => (await api.get('/api/quest-admin/access')).data.admin === true,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

const adminQuery = (key, url, params, options = {}) => ({
  queryKey: ['quest', 'admin', ...key],
  queryFn: async () => (await api.get(`/api/quest-admin${url}`, { params })).data,
  staleTime: 0,
  retry: false,
  ...options,
});

export const useAdminQuests = () => useQuery(adminQuery(['quests'], '/quests'));
export const useAdminQuest = (id) => useQuery(
  adminQuery(['quest', String(id)], `/quests/${id}`, undefined, { enabled: Boolean(id) }),
);
export const useAdminPeople = () => useQuery(adminQuery(['people'], '/people'));
export const useAdminPerson = (oid) => useQuery(
  adminQuery(['person', oid], `/people/${oid}`, undefined, { enabled: Boolean(oid) }),
);
export const useAdminLeaderboard = (period, cluster) => useQuery(
  adminQuery(['leaderboard', period || 'current', cluster || 'all'], '/leaderboard',
    { ...(period ? { period } : {}), ...(cluster ? { cluster } : {}) }),
);
export const useAdminChampions = (quarter) => useQuery(
  adminQuery(['champions', quarter || 'current'], '/champions', quarter ? { quarter } : {}),
);
export const useAdminPrizes = () => useQuery(adminQuery(['prizes'], '/prizes'));
export const useAdminPointsAudit = () => useQuery(adminQuery(['audit'], '/points/audit', { manual: 1 }));
export const useAdminPointValues = () => useQuery(adminQuery(['pointValues'], '/point-values'));
export const useAdminReport = (period) => useQuery(
  adminQuery(['report', period || 'current'], '/report', period ? { period } : {}),
);

// Every admin write refreshes everything Quest — points move the board, the
// people list, the reports and the employee screens together.
function useAdminMutation(fn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars) => (await fn(vars)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quest'] }),
  });
}

export const useSaveQuest = () => useAdminMutation(({ id, ...body }) => (id
  ? api.put(`/api/quest-admin/quests/${id}`, body)
  : api.post('/api/quest-admin/quests', body)));
export const usePublishQuest = () => useAdminMutation(
  ({ id, published }) => api.post(`/api/quest-admin/quests/${id}/publish`, { published }),
);
export const useDeleteQuest = () => useAdminMutation(({ id }) => api.delete(`/api/quest-admin/quests/${id}`));
export const useAwardTask = () => useAdminMutation(
  ({ id, entraUserId }) => api.post(`/api/quest-admin/quests/${id}/award`, { entra_user_id: entraUserId }),
);
export const useAdjustPoints = () => useAdminMutation((body) => api.post('/api/quest-admin/points', body));
// "Let them try again": one person's go at one Quest cleared (points taken back).
export const useResetQuestFor = () => useAdminMutation(
  ({ entraUserId, questId }) => api.post(`/api/quest-admin/people/${entraUserId}/quests/${questId}/reset`),
);

// Microsoft 365 profile photos for these people: { [id]: dataUrl | null }.
// Asked for 25 at a time (the server's limit), each batch kept for the day —
// a photo rarely changes — and kept out of the 'quest' key every save refreshes.
export function useAdminPhotos(ids) {
  const unique = [...new Set(ids.filter(Boolean))].sort();
  const batches = [];
  for (let i = 0; i < unique.length; i += 25) batches.push(unique.slice(i, i + 25));
  const results = useQueries({
    queries: batches.map((batch) => ({
      queryKey: ['questPhotos', batch.join(',')],
      queryFn: async () => (await api.get('/api/quest-admin/photos', { params: { ids: batch.join(',') } })).data.photos,
      staleTime: 12 * 60 * 60 * 1000,
      gcTime: 24 * 60 * 60 * 1000,
      retry: false,
    })),
  });
  return Object.assign({}, ...results.map((r) => r.data || {}));
}
// status: pending | approved | rejected | clarify; omitted = all, pending first
export const useAdminNominations = (status) => useQuery(
  adminQuery(['nominations', status || 'all'], '/nominations', status ? { status } : undefined),
);
// { id, decision: 'approve' | 'reject' | 'clarify', note }
export const useReviewNomination = () => useAdminMutation(
  ({ id, ...body }) => api.post(`/api/quest-admin/nominations/${id}/review`, body),
);
// Compliments, improvement ideas, colleague recognition and challenge entries.
// status: pending | reviewed | approved | rejected | clarify; kind: compliment |
// improvement | colleague | task; omitted = all, waiting first.
export const useAdminSubmissions = (status, kind) => useQuery(
  adminQuery(['submissions', status || 'all', kind || 'all'], '/submissions',
    { ...(status ? { status } : {}), ...(kind ? { kind } : {}) }),
);
// One, with its photo or document (only loaded when opened).
export const useAdminSubmission = (id, enabled) => useQuery(
  adminQuery(['submission', String(id)], `/submissions/${id}`, undefined, { enabled: Boolean(id && enabled) }),
);
// { id, decision: 'approve' | 'reject' | 'clarify' | 'review', note }
export const useReviewSubmission = () => useAdminMutation(
  ({ id, ...body }) => api.post(`/api/quest-admin/submissions/${id}/review`, body),
);
// The home screen's artwork banner. { image: data URL | null }
export const useAdminBanner = () => useQuery(adminQuery(['banner'], '/settings/banner'));
export const useSetBanner = () => useAdminMutation(({ image }) => api.put('/api/quest-admin/settings/banner', { image }));
export const useAddPrize = () => useAdminMutation((body) => api.post('/api/quest-admin/prizes', body));
export const useDeletePrize = () => useAdminMutation(({ id }) => api.delete(`/api/quest-admin/prizes/${id}`));
export const useUpdatePointValue = () => useAdminMutation(
  ({ activity, ...body }) => api.put(`/api/quest-admin/point-values/${activity}`, body),
);

// What each badge's reveal says in the FSM app: { [badgeKey]: { value, word,
// messages, customised, updated_by_name, updated_at, defaults } }.
export const useAdminBadgeMessages = () => useQuery(adminQuery(['badge-messages'], '/badge-messages'));
export const useSaveBadgeMessages = () => useAdminMutation(
  ({ key, ...body }) => api.put(`/api/quest-admin/badge-messages/${key}`, body),
);
export const useResetBadgeMessages = () => useAdminMutation(
  ({ key }) => api.delete(`/api/quest-admin/badge-messages/${key}`),
);

// The messages on prizes' achievement cards in the FSM app: { prizes: {
// [award]: { messages: [{ value, text }], customised, updated_by_name,
// updated_at, defaults } }, values: Nano's six }.
export const useAdminPrizeMessages = () => useQuery(adminQuery(['prize-messages'], '/prize-messages'));
export const useSavePrizeMessages = () => useAdminMutation(
  ({ key, ...body }) => api.put(`/api/quest-admin/prize-messages/${key}`, body),
);
export const useResetPrizeMessages = () => useAdminMutation(
  ({ key }) => api.delete(`/api/quest-admin/prize-messages/${key}`),
);
